import { SubmitHandler, useForm } from 'react-hook-form';
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import StyledInput from '../../../components/common/form/styled-input';
import StyledSwitch from '../../../components/common/form/styled-switch';
import CommonButtonDefault from '../../../components/common/buttons/default';
import * as albumsService from '../../../services/api/albums';
import { useAuth } from '../../../hooks/use-auth';
import AlertPrimary from '../../../components/common/alerts/alert-primary';
import CommonAnimatedSpinner from '../../../components/common/animated/spinner';
import { strings } from '../../../localization/strings';
import LabelWithError from '../../../components/common/form/label-with-error';

type Inputs = {
  title: string;
  publish: number;
};

interface ComponentProps {
  photoIds: number[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function CreateAlbumFromPhotosForm({ photoIds, onClose, onSuccess }: ComponentProps) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const { token } = useAuth();
  const queryClient = useQueryClient();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: { publish: 1 },
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setError(undefined);

    try {
      setIsLoading(true);
      await albumsService.createFromPhotos(token, data.title, photoIds, !!data.publish);

      await queryClient.invalidateQueries({ queryKey: ['localPhotos'] });
      await queryClient.invalidateQueries({ queryKey: ['albums'] });

      setIsLoading(false);
      onSuccess();
    } catch (e) {
      setIsLoading(false);
      if (e instanceof Error) {
        setError(e.message);
      }
    }
  };

  return (
    <div className="w-full sm:w-96 pt-2">
      {isLoading && (
        <div className="py-2 text-xs">
          <CommonAnimatedSpinner sm />
        </div>
      )}

      <p className="text-sm text-gray-600 mb-3">
        {strings.album.filter.selected} {photoIds.length}
      </p>

      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <LabelWithError title={strings.album.form.label.title} error={errors.title?.message}>
          <StyledInput {...register('title', { required: true })} />
        </LabelWithError>

        <div className="flex items-center space-x-3">
          <StyledSwitch control={control} name="publish" />
          <span className="text-sm font-medium text-gray-900">{strings.album.fromPhotos.publishAfterSave}</span>
        </div>

        {error && <AlertPrimary type="danger" text={error} />}

        <div className="py-3 flex justify-end space-x-4">
          <CommonButtonDefault type="button" secondary onClick={onClose}>
            {strings.button.cancel}
          </CommonButtonDefault>
          <CommonButtonDefault primary type="submit" disabled={isLoading || photoIds.length === 0}>
            {strings.button.create}
          </CommonButtonDefault>
        </div>
      </form>
    </div>
  );
}
